import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle, ArrowRight, Mail, Radar } from "lucide-react";

const CELLS = [
  ["visitors", "VISITORS"], ["coach_clicks", "COACH CLICKS"], ["profiles_claimed", "CLAIMS"],
  ["profiles_completed", "COMPLETED"], ["jobs_posted", "JOBS POSTED"], ["matches_generated", "MATCHES"],
  ["paid_conversions", "PAID"], ["revenue", "REVENUE $"],
];

export const FunnelHealth = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/funnel/today").then(({ data }) => setData(data)).catch(() => {});
  }, []);

  if (!data) return null;
  return (
    <div className="mt-6" data-testid="funnel-health-panel">
      <div className="flex items-center justify-between">
        <h2 className="heading text-lg">TODAY — FUNNEL HEALTH</h2>
        <Link to="/hq/funnel" data-testid="funnel-health-link" className="text-[10px] tracking-widest text-volt flex items-center gap-1 hover:underline">
          FULL FUNNEL <ArrowRight size={11} />
        </Link>
      </div>
      <div className="mt-3 grid grid-cols-4 md:grid-cols-8 gap-2" data-testid="funnel-today-grid">
        {CELLS.map(([k, label]) => (
          <div key={k} className="border border-neutral-800 bg-appcard p-3 text-center" data-testid={`today-${k}`}>
            <div className={`heading text-xl ${k === "revenue" && data.today[k] > 0 ? "text-emerald-400" : "text-white"}`}>
              {k === "revenue" ? `$${data.today[k]}` : data.today[k]}
            </div>
            <div className="text-[8px] tracking-widest text-neutral-500 mt-0.5">{label}</div>
          </div>
        ))}
      </div>
      {data.needs_attention?.length > 0 && (
        <div className="mt-3 border border-neutral-800 bg-[#0C0C0E] divide-y divide-neutral-900" data-testid="needs-attention-list">
          <div className="px-4 py-2 text-[10px] tracking-widest text-neutral-500">NEEDS ATTENTION</div>
          {data.needs_attention.map((n, i) => (
            <Link key={i} to={n.href} data-testid={`needs-attention-${i}`}
              className="flex items-center gap-3 px-4 py-2.5 text-xs hover:bg-neutral-900 transition-colors">
              <AlertTriangle size={13} className={n.level === "red" ? "text-red-400" : "text-amber-400"} />
              <span className="text-neutral-300">{n.label}</span>
              <ArrowRight size={11} className="ml-auto text-neutral-600" />
            </Link>
          ))}
        </div>
      )}
      <div className="mt-3 grid md:grid-cols-2 gap-3">
        {data.hunter && <HunterCard h={data.hunter} />}
        {data.email_quota && <QuotaCard q={data.email_quota} />}
      </div>
    </div>
  );
};

const HunterCard = ({ h }) => (
  <div className="border border-neutral-800 bg-appcard p-4" data-testid="hunter-progress-card">
    <div className="flex items-center justify-between">
      <div className="text-[10px] tracking-widest text-neutral-500 flex items-center gap-1.5"><Radar size={11} className="text-volt" /> AI EMAIL HUNTER — LAST 24H</div>
      <Link to="/hq/queue?bucket=needs_email" data-testid="hunter-run-link" className="text-[10px] tracking-widest text-volt hover:underline">RUN A BATCH</Link>
    </div>
    <div className="mt-3 grid grid-cols-4 gap-2 text-center">
      {[
        ["pages_found", "BIO PAGES FOUND", `${h.pages_found}`, `of ${h.pages_searched} searched`],
        ["emails_found", "EMAILS FOUND", `${h.emails_found + (h.staff_dir_emails || 0)}`, `${h.staff_dir_emails || 0} via staff directories`],
        ["queue_refilled", "QUEUE REFILLS", `${h.queue_refilled}`, `${h.staff_dir_new_coaches || 0} new coaches scraped`],
        ["bios_enriched", "BIOS ENRICHED", `${h.bios_enriched ?? 0}`, `${h.bios_pending ?? 0} waiting · CIR refreshed`],
      ].map(([k, label, val, sub]) => (
        <div key={k} data-testid={`hunter-${k}`}>
          <div className={`heading text-2xl ${Number(val) > 0 ? "text-emerald-400" : "text-white"}`}>{val}</div>
          <div className="text-[8px] tracking-widest text-neutral-500 mt-0.5">{label}</div>
          <div className="text-[9px] text-neutral-600">{sub}</div>
        </div>
      ))}
    </div>
    <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
      <div className="text-[10px] text-neutral-500" data-testid="hunter-pool">
        Pool left: <span className="text-neutral-300">{h.discoverable_pool}</span> coaches need a bio page · <span className="text-neutral-300">{h.extractable_pool}</span> pages ready for extraction
        {h.last_run_at && <> · last run {h.last_run_at.slice(0, 16).replace("T", " ")} UTC</>}
      </div>
      <button data-testid="enrich-bios-now-btn"
        onClick={async () => {
          if (!window.confirm("Re-read up to 200 coach bio pages now with Claude Haiku and refresh their CIR? (~7 min, runs in background; also runs nightly for 50)")) return;
          try {
            const { data } = await api.post("/admin/bio-enrich/run?limit=200");
            toast.success(`Bio enrichment started — ${data.eligible_now} coaches with a bio page are waiting`, { description: data.note, duration: 9000 });
          } catch (e) { toast.error(e.response?.data?.detail || "Could not start enrichment"); }
        }}
        className="min-h-[32px] px-3 border border-emerald-500/70 text-emerald-300 heading text-[9px] tracking-widest hover:bg-emerald-500 hover:text-black transition-colors">
        ENRICH 200 BIOS NOW
      </button>
    </div>
  </div>
);

const QuotaCard = ({ q }) => {
  const pct = Math.min(q.pct, 100);
  const tone = q.pct >= 95 ? "bg-red-500" : q.pct >= 80 ? "bg-amber-400" : "bg-volt";
  return (
    <div className="border border-neutral-800 bg-appcard p-4" data-testid="email-quota-card">
      <div className="flex items-center justify-between">
        <div className="text-[10px] tracking-widest text-neutral-500 flex items-center gap-1.5"><Mail size={11} className="text-volt" /> EMAIL QUOTA — TODAY (UTC)</div>
        <div className={`heading text-sm ${q.pct >= 80 ? "text-amber-300" : "text-white"}`} data-testid="quota-used">{q.total}<span className="text-neutral-500">/{q.quota}</span></div>
      </div>
      <div className="mt-2 h-1.5 bg-neutral-900 overflow-hidden"><div className={`h-full ${tone} transition-[width] duration-700`} style={{ width: `${pct}%` }} /></div>
      <div className="mt-3 space-y-1.5" data-testid="quota-breakdown">
        {q.categories.length === 0 && <div className="text-[10px] text-neutral-600">Nothing sent yet today.</div>}
        {q.categories.map((c) => (
          <div key={c.key} className="flex items-center gap-2 text-[11px]" data-testid={`quota-${c.key}`}>
            <span className="text-neutral-400 w-44 truncate">{c.label}</span>
            <div className="flex-1 h-1 bg-neutral-900"><div className={`h-full ${c.cap && c.count >= c.cap ? "bg-amber-400" : "bg-neutral-500"}`} style={{ width: `${c.cap ? Math.min(100, (100 * c.count) / c.cap) : q.total ? (100 * c.count) / q.total : 0}%` }} /></div>
            <span className="heading text-neutral-200 w-14 text-right">{c.count}{c.cap ? <span className="text-neutral-600 font-normal">/{c.cap}</span> : null}</span>
          </div>
        ))}
      </div>
      {q.allocation && (
        <div className="mt-2 text-[9px] text-neutral-600" data-testid="quota-allocation-note">
          Allocation: claim invites first at 15:00 UTC (cap {q.allocation.claim_drip}); routine updates & reminders capped lower, lower still on supply days.
        </div>
      )}
    </div>
  );
};
